import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, catchError, from, of, switchMap, throwError } from 'rxjs';
import { EncryptedEndpoint, findEncryptedEndpoint } from '@app/security/encrypted-endpoints';
import { EncryptionError, EncryptionService } from '@app/services/encryption.service';
import { RsaKeyService } from '@app/services/rsa-key.service';

/** Header con la pública RSA de la sesión del kiosko (PEM en Base64). */
export const CLIENT_PUBLIC_KEY_HEADER = 'x-client-public-key';

/** Sobre del esquema RSA híbrido: llave AES cifrada con RSA, IV y datos cifrados con AES. */
interface HybridEnvelope {
  key: string;
  iv: string;
  data: string;
}

/**
 * Cifra el request y descifra la respuesta de los endpoints registrados en
 * `ENCRYPTED_ENDPOINTS` con el esquema RSA híbrido (el mismo de CUSS). Cualquier
 * otro request pasa sin cambios.
 *
 * Se registra DESPUÉS de ApiInterceptor (queda como el más interno): ApiInterceptor
 * ve el request y la respuesta en claro, y SsbdHttp503Interceptor ve el status original.
 *
 * - Request: siempre manda `x-client-public-key`. En `query`/`body` arma el sobre
 *   `{key, iv, data}`: el payload se cifra con una llave AES de un solo uso y esa
 *   llave se cifra con la pública fija del BFF (ver RsaKeyService).
 * - Respuesta 2xx: llega el sobre `{key, iv, data}`; `key` viene cifrada con la
 *   pública de la sesión y se descifra con su privada.
 * - Los errores 4xx/5xx llegan en claro; como se piden como texto, se parsean de
 *   vuelta a objeto para que `ErrorAdapter` funcione igual que antes.
 * - Fail closed: si el cifrado falla, el request no se envía y se emite un
 *   HttpErrorResponse genérico (código '99').
 */
@Injectable()
export class ApiEncryptionInterceptor implements HttpInterceptor {
  constructor(
    private readonly _encryption: EncryptionService,
    private readonly _rsa: RsaKeyService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const endpoint = findEncryptedEndpoint(req.method, req.url);
    if (!endpoint) {
      return next.handle(req);
    }

    return from(this.encryptRequest(req, endpoint)).pipe(
      switchMap((encryptedReq) => next.handle(encryptedReq)),
      switchMap((event) =>
        event instanceof HttpResponse ? from(this.decryptResponse(event, endpoint)) : of(event)
      ),
      catchError((error) => throwError(() => this.toHttpError(error, req)))
    );
  }

  private async encryptRequest(
    req: HttpRequest<unknown>,
    endpoint: EncryptedEndpoint
  ): Promise<HttpRequest<unknown>> {
    const publicKey = await this._rsa.getPublicKeyBase64();
    const textReq = req.clone({
      responseType: 'text',
      setHeaders: { [CLIENT_PUBLIC_KEY_HEADER]: publicKey },
    });

    switch (endpoint.request) {
      case 'query': {
        const plain = JSON.stringify(paramsToObject(req.params));
        const data = await this.buildEnvelope(plain, endpoint);
        return textReq.clone({ params: new HttpParams().set('data', data) });
      }
      case 'body': {
        const plain = JSON.stringify(req.body ?? {});
        const data = await this.buildEnvelope(plain, endpoint);
        return textReq.clone({ body: data, setHeaders: { 'Content-Type': 'application/json' } });
      }
      default:
        return textReq;
    }
  }

  private async buildEnvelope(plain: string, endpoint: EncryptedEndpoint): Promise<string> {
    const { keyB64, ivB64 } = this._encryption.generateAesMaterial();
    const data = await this._encryption.encryptWithKey(plain, keyB64, ivB64);
    const key = await this._rsa.encryptForBff(endpoint.domain, keyB64);
    const envelope: HybridEnvelope = { key, iv: ivB64, data };
    return JSON.stringify(envelope);
  }

  private async decryptResponse(
    event: HttpResponse<unknown>,
    endpoint: EncryptedEndpoint
  ): Promise<HttpResponse<unknown>> {
    const raw = typeof event.body === 'string' ? event.body.trim() : '';
    if (!raw) {
      return event.clone({ body: null });
    }

    const envelope = parseJson<HybridEnvelope>(raw);
    if (!isEnvelope(envelope)) {
      throw new EncryptionError(
        'DECRYPT_FAILED',
        `Respuesta cifrada con formato inválido (${endpoint.id})`
      );
    }

    const aesKey = await this._rsa.decryptAesKey(envelope.key);
    const plain = await this._encryption.decryptWithKey(envelope.data, aesKey, envelope.iv);
    const body = parseJson(plain);
    if (body === undefined) {
      throw new EncryptionError(
        'DECRYPT_FAILED',
        `La respuesta descifrada no es JSON (${endpoint.id})`
      );
    }
    return event.clone({ body });
  }

  private toHttpError(error: unknown, req: HttpRequest<unknown>): unknown {
    if (error instanceof EncryptionError) {
      return new HttpErrorResponse({
        status: 0,
        statusText: 'Encryption Error',
        url: req.url,
        // Forma que ErrorAdapter trata como error genérico ('99'); no incluye payload ni llave.
        error: { code: '99', msg: error.message, encryptionCode: error.code },
      });
    }

    if (error instanceof HttpErrorResponse && typeof error.error === 'string') {
      const parsed = parseJson(error.error);
      if (parsed !== undefined) {
        return new HttpErrorResponse({
          error: parsed,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        });
      }
    }

    return error;
  }
}

function isEnvelope(value: unknown): value is HybridEnvelope {
  const envelope = value as HybridEnvelope | undefined;
  return (
    typeof envelope?.key === 'string' &&
    typeof envelope.iv === 'string' &&
    typeof envelope.data === 'string'
  );
}

function paramsToObject(params: HttpParams): Record<string, string | string[]> {
  const result: Record<string, string | string[]> = {};
  for (const key of params.keys()) {
    const values = params.getAll(key) ?? [];
    result[key] = values.length > 1 ? values : values[0];
  }
  return result;
}

function parseJson<T = unknown>(text: string): T | undefined {
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined;
  }
}
